import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dices, Gift, MinusCircle } from "lucide-react";
import { api, DiscordChannel, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/MultiSelect";
import { Card } from "@/components/ui/card";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FALLBACK_GAMES: GuildConfig["games"] = {
  enabled: true,
  currency_name: "Coins",
  ui_style: "embed",
  allowed_channel_ids: [],
  roulette: { enabled: true, min_bet: 1, max_bet: 10_000 },
  slots: { enabled: true, min_bet: 1, max_bet: 5_000 },
  coinflip: { enabled: true, min_bet: 1, max_bet: 5_000 },
  blackjack: { enabled: true, min_bet: 1, max_bet: 100_000 },
  tictactoe: { enabled: true, min_bet: 1, max_bet: 50_000 },
  battleship: { enabled: true, min_bet: 1, max_bet: 50_000 },
  memory: { enabled: true, min_bet: 1, max_bet: 50_000 },
  mines: { enabled: true, min_bet: 1, max_bet: 50_000 },
  dice: { enabled: true, min_bet: 1, max_bet: 50_000 },
  duck: { enabled: true, min_bet: 1, max_bet: 50_000 },
  hangman: { enabled: true, min_bet: 1, max_bet: 50_000, word_language: "english" },
  horseracing: { enabled: true, min_bet: 1, max_bet: 50_000 },
  game_daily: { enabled: true, amount: 50 },
  leaderboard: {
    enabled: false,
    channel_id: "",
    thread_id: "",
    message_id: "",
    embed_color: "#F0B132",
    top_n: 10,
  },
  promotion_enabled: false,
  promotion_channel_id: "",
  promotion_interval: 12,
};

function coalesceGames(
  raw: GuildConfig["games"] | null | undefined,
): GuildConfig["games"] {
  if (!raw) {
    return {
      ...FALLBACK_GAMES,
      roulette: { ...FALLBACK_GAMES.roulette },
      slots: { ...FALLBACK_GAMES.slots },
      coinflip: { ...FALLBACK_GAMES.coinflip },
      blackjack: { ...FALLBACK_GAMES.blackjack },
      tictactoe: { ...FALLBACK_GAMES.tictactoe },
      battleship: { ...FALLBACK_GAMES.battleship },
      memory: { ...FALLBACK_GAMES.memory },
      mines: { ...FALLBACK_GAMES.mines },
      dice: { ...FALLBACK_GAMES.dice },
      duck: { ...FALLBACK_GAMES.duck },
      hangman: { ...FALLBACK_GAMES.hangman },
      horseracing: { ...FALLBACK_GAMES.horseracing },
      game_daily: { ...FALLBACK_GAMES.game_daily },
      leaderboard: { ...FALLBACK_GAMES.leaderboard },
    };
  }
  return {
    ...FALLBACK_GAMES,
    ...raw,
    allowed_channel_ids: Array.isArray(raw.allowed_channel_ids)
      ? raw.allowed_channel_ids
      : [],
    ui_style:
      raw.ui_style === "sections" || raw.ui_style === "embed"
        ? raw.ui_style
        : FALLBACK_GAMES.ui_style,
    roulette: { ...FALLBACK_GAMES.roulette, ...(raw.roulette || {}) },
    slots: { ...FALLBACK_GAMES.slots, ...(raw.slots || {}) },
    coinflip: { ...FALLBACK_GAMES.coinflip, ...(raw.coinflip || {}) },
    blackjack: { ...FALLBACK_GAMES.blackjack, ...(raw.blackjack || {}) },
    tictactoe: { ...FALLBACK_GAMES.tictactoe, ...(raw.tictactoe || {}) },
    battleship: { ...FALLBACK_GAMES.battleship, ...(raw.battleship || {}) },
    memory: { ...FALLBACK_GAMES.memory, ...(raw.memory || {}) },
    mines: { ...FALLBACK_GAMES.mines, ...(raw.mines || {}) },
    dice: { ...FALLBACK_GAMES.dice, ...(raw.dice || {}) },
    duck: { ...FALLBACK_GAMES.duck, ...(raw.duck || {}) },
    hangman: { ...FALLBACK_GAMES.hangman, ...(raw.hangman || {}) },
    horseracing: { ...FALLBACK_GAMES.horseracing, ...(raw.horseracing || {}) },
    game_daily: { ...FALLBACK_GAMES.game_daily, ...(raw.game_daily || {}) },
    leaderboard: { ...FALLBACK_GAMES.leaderboard, ...(raw.leaderboard || {}) },
    promotion_enabled: raw.promotion_enabled ?? FALLBACK_GAMES.promotion_enabled,
    promotion_channel_id: raw.promotion_channel_id || FALLBACK_GAMES.promotion_channel_id,
    promotion_interval: raw.promotion_interval || FALLBACK_GAMES.promotion_interval,
  };
}

type Props = {
  guildId: string;
  config: GuildConfig;
  channels: DiscordChannel[];
  onChangeGames: (v: GuildConfig["games"]) => void;
};

const CMDS = [
  { a: "/balance", b: "Show balance & W/L" },
  { a: "/gameclaim", b: "Daily free coins (20h)" },
  { a: "/givecoins", b: "Staff: add coins (Manage Server) — or use panel below" },
  { a: "/takecoins", b: "Staff: remove up to this many coins" },
  { a: "/roulette", b: "R/B 1:1  ·  green = only 0 (35:1)" },
  { a: "/roulette_straight", b: "Single number 0–36 (35:1)" },
  { a: "/rstraight", b: "Same as /roulette_straight (shorter name)" },
  { a: "/slots", b: "3-reel slots" },
  { a: "/coinflip", b: "Heads or tails" },
  { a: "/blackjack", b: "vs house — Hit/Stand, 3:2 on natural" },
  { a: "/tictactoe bet", b: "Tic Tac Toe vs bot — newest 3 marks stay, no draw farming" },
  { a: "/battleship bet", b: "5x5 Battleship vs bot fleet — find all ships" },
  { a: "/memory bet", b: "Memory Match vs bot — most pairs wins" },
  { a: "/mines bet [mines]", b: "5x5 board. Bombs are random 3-10 unless selected" },
  { a: "/dice bet mode target", b: "Roll 1–100 over/under your target" },
  { a: "/duck bet", b: "Duck Crossing — move forward, avoid cars, cash out anytime" },
  { a: "/hangman bet", b: "Guess the word before the hangman drawing is complete" },
  { a: "/horseracing bet", b: "Bet on horses and watch them race in real-time" },
  { a: "/leaderboard", b: "Top players (chat)" },
  { a: "/setlbcolor", b: "Manage Server — embed color (hex)" },
];

export function GamesCommunityTab({
  guildId,
  config,
  channels,
  onChangeGames,
}: Props) {
  const [econUserId, setEconUserId] = useState("");
  const [econAmount, setEconAmount] = useState(100);
  const g = coalesceGames(config.games);
  const ug = <K extends keyof typeof g>(k: K, v: (typeof g)[K]) =>
    onChangeGames({ ...g, [k]: v });
  const uSub = (name: "roulette" | "slots" | "coinflip" | "blackjack" | "tictactoe" | "battleship" | "memory" | "mines" | "dice" | "duck" | "hangman" | "horseracing", field: "enabled" | "min_bet" | "max_bet", val: boolean | number) => {
    const sub = { ...g[name], [field]: val };
    onChangeGames({ ...g, [name]: sub });
  };
  const uHangmanLanguage = (word_language: "english" | "slovenian" | "mixed") => {
    onChangeGames({ ...g, hangman: { ...g.hangman, word_language } });
  };
  const ulb = <K extends keyof typeof g.leaderboard>(k: K, v: (typeof g.leaderboard)[K]) => {
    onChangeGames({ ...g, leaderboard: { ...g.leaderboard, [k]: v } });
  };
  const ugd = (field: "enabled" | "amount", val: boolean | number) => {
    onChangeGames({ ...g, game_daily: { ...g.game_daily, [field]: val } });
  };

  const grantMut = useMutation({
    mutationFn: () =>
      api.gamesGrantCoins(guildId, {
        user_id: econUserId.trim(),
        amount: Math.floor(econAmount) || 0,
      }),
    onSuccess: (d) => {
      toast.success(
        `Added ${d.granted.toLocaleString()} ${g.currency_name || "coins"}. New balance: ${d.new_balance.toLocaleString()}.`,
      );
    },
    onError: (e: Error) => toast.error(e.message || "Request failed"),
  });
  const takeMut = useMutation({
    mutationFn: () =>
      api.gamesTakeCoins(guildId, {
        user_id: econUserId.trim(),
        amount: Math.floor(econAmount) || 0,
      }),
    onSuccess: (d) => {
      toast.success(
        `Removed ${d.removed.toLocaleString()}. New balance: ${d.new_balance.toLocaleString()}.`,
      );
    },
    onError: (e: Error) => toast.error(e.message || "Request failed"),
  });

  const text = channels.filter((ch) => ch.type === 0);
  return (
    <div className="space-y-8">
      <Section
        icon={Dices}
        title="Fun & games"
        description="Roulette, slots, coinflip, blackjack, Tic Tac Toe, and Battleship use the same economy. Data: games_economy.json. Live leaderboard every 5 min if configured. Staff: /givecoins, /takecoins, or the grant card below. All slash names are listed in the panel below."
        enabled={g.enabled}
        onToggle={(v) => ug("enabled", v)}
      >
        <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
          <p className="font-medium text-foreground">How it works</p>
          <p>
            Turn games on, set <strong>currency</strong> and <strong>min/max bets</strong> per game, and optionally
            limit betting to specific text channels. Members use the <strong>slash commands</strong> in the list at the
            bottom of this section. Staff with <strong>Manage Server</strong> can use <code className="text-primary">/givecoins</code> and <code className="text-primary">/takecoins</code> (or the grant form below). <code className="text-primary">/claim</code> is the free daily.
          </p>
        </div>

        <Card className="p-4 space-y-2 border-border/50 border-primary/20 bg-primary/5 text-sm">
          <h4 className="font-medium">Discord: roulette & sync</h4>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <code className="text-primary">/roulette</code> bets on red or black (1:1) or green/0 (35:1) — there's no
            single-number "straight" bet. If a slash does not show in your client, the host must{" "}
            <strong>restart the bot</strong> after update so command tree sync runs; it can take up to about an hour
            for Discord to show changes everywhere. The full list of casino commands is in the box at the bottom of
            this section.
          </p>
        </Card>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 md:items-start [&>*]:min-w-0">
          <div className="order-1 md:order-1">
            <Field
              label="Currency name"
              hint="Shown in /balance, embeds, leaderboard. New players start with 100 (see /balance)."
            >
              <Input
                value={g.currency_name}
                onChange={(e) => ug("currency_name", e.target.value)}
              />
            </Field>
          </div>
          <div className="order-3 col-span-2 md:order-2 md:col-span-1">
            <Field
              label="Command channels (optional)"
              hint="If empty, betting works in any text channel. If you pick channels, only casino/game commands are limited; /balance, /gameclaim, and /leaderboard work everywhere (when games are enabled)."
            >
              <MultiSelect
                options={text.map((ch) => ({ id: ch.id, name: ch.name }))}
                value={g.allowed_channel_ids}
                onChange={(v) => ug("allowed_channel_ids", v)}
                prefix="#"
              />
            </Field>
          </div>
          <div className="order-2 md:order-3">
            <Field
              label="Game message layout"
              hint="Classic embed vs Discord Components v2 (one framed panel + buttons)."
            >
              <Select
                value={g.ui_style === "sections" ? "sections" : "embed"}
                onValueChange={(v) =>
                  onChangeGames({
                    ...g,
                    ui_style: v as "embed" | "sections",
                  })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="embed">Classic embed</SelectItem>
                  <SelectItem value="sections">Sections (v2 panel)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-start p-4 rounded-lg border border-primary/10 bg-primary/5">
          <div className="col-span-full">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Gift className="h-4 w-4 text-primary" />
              Game Promotions
            </h4>
            <p className="text-xs text-muted-foreground mt-1">
              Automatically post an embed about available games to encourage players.
            </p>
          </div>
          
          <Field label="Auto-Promote" hint="Enable periodic game ads?">
            <label className="flex items-center gap-2 text-sm mt-2">
              <input
                type="checkbox"
                checked={!!g.promotion_enabled}
                onChange={(e) => ug("promotion_enabled", e.target.checked)}
                className="rounded"
              />
              Enabled
            </label>
          </Field>

          <Field label="Promo Channel" hint="Where to send the ads.">
            <ChannelPicker
              channels={text}
              value={g.promotion_channel_id || ""}
              onChange={(v) => ug("promotion_channel_id", v === "__none__" ? "" : v)}
            />
          </Field>

          <Field label="Interval (Hours)" hint="How often to post (e.g. 12, 24).">
            <Input
              type="number"
              min={1}
              max={168}
              value={g.promotion_interval ?? 12}
              onChange={(e) => ug("promotion_interval", Number(e.target.value) || 12)}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
          {(["roulette", "slots", "coinflip", "blackjack", "tictactoe", "battleship", "memory", "mines", "dice", "duck", "hangman", "horseracing"] as const).map((name) => {
            const s = g[name];
            const label = name === "tictactoe" ? "Tic Tac Toe" : name === "memory" ? "Memory Match" : name;
            return (
              <Card key={name} className="p-4 space-y-2 border-border/50">
                <div className="font-medium capitalize">{label}</div>
                {name === "blackjack" && (
                  <p className="text-xs text-muted-foreground leading-snug">
                    In Discord, <strong>Split / Double</strong> are on the table message (not toggles here). Split needs a
                    pair in value and your <strong>balance must be at least 2×</strong> the <code className="text-foreground/90">/blackjack</code> bet you use — if the button is
                    grey, try a <strong>smaller bet</strong> or get more {g.currency_name || "coins"}.
                  </p>
                )}
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={s.enabled}
                    onChange={(e) => uSub(name, "enabled", e.target.checked)}
                    className="rounded"
                  />
                  Enabled
                </label>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Min</div>
                    <Input
                      type="number"
                      min={1}
                      value={s.min_bet}
                      onChange={(e) =>
                        uSub(name, "min_bet", Number(e.target.value) || 1)
                      }
                    />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Max</div>
                    <Input
                      type="number"
                      min={1}
                      value={s.max_bet}
                      onChange={(e) =>
                        uSub(name, "max_bet", Number(e.target.value) || 1)
                      }
                    />
                  </div>
                </div>
                {name === "hangman" && (
                  <Field
                    label="Word language"
                    hint="Choose which word pool /hangman uses on this server."
                  >
                    <Select
                      value={g.hangman.word_language || "english"}
                      onValueChange={(v) =>
                        uHangmanLanguage(v as "english" | "slovenian" | "mixed")
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="english">English words</SelectItem>
                        <SelectItem value="slovenian">Slovenian words</SelectItem>
                        <SelectItem value="mixed">Mixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                )}
              </Card>
            );
          })}
        </div>
        <Field label="Daily /claim" hint="Coins added each claim.">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={g.game_daily.enabled}
                onChange={(e) => ugd("enabled", e.target.checked)}
                className="rounded"
              />
              On
            </label>
            <Input
              type="number"
              className="w-28"
              value={g.game_daily.amount}
              onChange={(e) => ugd("amount", Number(e.target.value) || 0)}
            />
            <span className="text-xs text-muted-foreground">coins / claim (20h)</span>
          </div>
        </Field>
        <Card className="p-4 space-y-3 border-border/50">
          <div className="font-medium">Live leaderboard (embed)</div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={g.leaderboard.enabled}
              onChange={(e) => ulb("enabled", e.target.checked)}
              className="rounded"
            />
            Post & refresh every 5 min in channel below
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Channel" hint="Bot will create/edit one message.">
              <ChannelPicker
                channels={text}
                value={g.leaderboard.channel_id}
                onChange={(v) => ulb("channel_id", v === "__none__" ? "" : v)}
              />
            </Field>
            <Field
              label="Thread ID (optional)"
              hint="Paste a forum/ thread ID for your ‘winner’ brag thread. Color is set in panel or /setlbcolor."
            >
              <Input
                value={g.leaderboard.thread_id}
                onChange={(e) => ulb("thread_id", e.target.value.replace(/\D/g, ""))}
                placeholder="1234567890..."
              />
            </Field>
            <Field label="Top N">
              <Input
                type="number"
                min={1}
                max={25}
                value={g.leaderboard.top_n}
                onChange={(e) => ulb("top_n", Math.min(25, Math.max(1, Number(e.target.value) || 10)))}
              />
            </Field>
            <Field label="Embed color (hex)" hint="#RRGGBB. Members with Manage Server can /setlbcolor.">
              <Input
                value={g.leaderboard.embed_color}
                onChange={(e) => ulb("embed_color", e.target.value.substring(0, 7))}
                placeholder="#F0B132"
                maxLength={7}
                className="font-mono"
              />
            </Field>
            <Field
              label="Message ID (read-only if bot created)"
              hint="Set automatically after the first run."
            >
              <Input
                className="opacity-70"
                value={g.leaderboard.message_id}
                onChange={() => null}
                readOnly
                placeholder="auto"
              />
            </Field>
          </div>
        </Card>
        <Card className="p-4 space-y-3 border-border/50 border-amber-500/20 bg-amber-500/5">
          <div>
            <h4 className="font-medium flex items-center gap-2">Grant or remove coins (this dashboard)</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Same balance file as the bot. Members earn coins with <code className="text-primary">/claim</code> (if
              on) and start with a small balance. Paste a numeric{" "}
              <strong>Discord user ID</strong> (User Settings → App Settings → Advanced → Developer Mode, then
              right‑click user → Copy User ID).
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="User ID" hint="Numbers only, e.g. 123456789012345678">
              <Input
                className="font-mono"
                value={econUserId}
                onChange={(e) => setEconUserId(e.target.value.replace(/\D/g, ""))}
                placeholder="Discord user id"
                inputMode="numeric"
              />
            </Field>
            <Field label="Amount" hint="1 – 1,000,000">
              <Input
                type="number"
                min={1}
                max={1_000_000}
                value={econAmount}
                onChange={(e) => setEconAmount(Math.max(1, Number(e.target.value) || 0))}
              />
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={grantMut.isPending || takeMut.isPending || econUserId.length < 5}
              onClick={() => grantMut.mutate()}
            >
              <Gift className="h-4 w-4" />
              Add coins
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={grantMut.isPending || takeMut.isPending || econUserId.length < 5}
              onClick={() => takeMut.mutate()}
            >
              <MinusCircle className="h-4 w-4" />
              Remove up to amount
            </Button>
          </div>
        </Card>
        <Card className="p-4 text-sm border-border/40 bg-muted/20">
          <h4 className="font-medium mb-2">Slash commands (English)</h4>
          <ul className="space-y-1 text-muted-foreground">
            {CMDS.map((x) => (
              <li key={x.a} className="font-mono text-xs">
                <code className="text-primary">{x.a}</code> {x.b}
              </li>
            ))}
          </ul>
        </Card>
      </Section>
    </div>
  );
}
